<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_FIXED_DEPOSITS_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              {{ $t('PDF') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(bookedAmount)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

      <template #cell(commencementDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>
      <template #cell(maturityDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>

      <template #cell(accruedInterest)="data">
        <span class="text-nowrap">
          {{ formatAmount(data.value) }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="fixedDepositBalanceRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Account Name')"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                :label="$t('Account Number')"
                :placeholder="$t('Enter Account Number')"
                type="text"
                for="account-number"
              />
            </b-col>
          <!-- Account Number -->
          </b-row>

          <b-row class="match-height">
            <b-col md="6 mt-2">
              <SwitchButton
                v-model="filter.useCurrentFinancialDate"
                :label="$t('Use Current Financial Date')"
                name="use-current-financial-date"
                :readonly="isReadonly"
                @input="setUseCurrentFinancialDate(filter.useCurrentFinancialDate)"
              />
            </b-col>
            <!-- Reporting Date -->
            <b-col md="6">
              <DatePickerField
                id="reporting-date"
                v-model="filter.financialDate"
                :min-date="new Date()"
                :label="$t('Reporting Date')"
                rules="required"
                :error-name="$t('Reporting Date')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
                :disabled="isDisabled"
              />
            </b-col>
            <!-- Reporting Date -->
          </b-row>

          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Account Officer -->
            <b-col md="6">
              <SelectField
                id="accountOfficer"
                v-model="filter.accountOfficerStaffID"
                :reduce="option => option.value"
                :label="$t('Account Officer')"
                :placeholder="$t('Select')"
                :options="accountOfficerOptions"
                item="label"
              />
            </b-col>
            <!-- Account Officer -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
              />
            </b-col>
          <!-- Branch -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SwitchButton from '../../../components/switch/Switch.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDate } from '../../../../helpers/formatDate'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    ValidationObserver,
    SwitchButton,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      isDisabled: false,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: 'Save',
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: 'Customer Account Balance Report',
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'FD Account Number' },
        { key: 'liquidationAccount', sortable: true, label: 'Liquidation Account Number' },
        { key: 'product', sortable: true, label: 'Product Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'tenure', sortable: true, label: 'Tenure (Days)' },
        { key: 'interestRate', sortable: true, label: 'Interest Rate' },
        { key: 'commencementDate', sortable: true, label: 'Commencement Date' },
        { key: 'maturityDate', sortable: true, label: 'Maturity Date' },
        { key: 'bookedAmount', sortable: true, label: 'Booked Amount' },
        { key: 'accruedInterest', sortable: true, label: 'Accrued Interest' },
      ],
      filter: {
        accountName: '',
        accountNumber: '',
        financialDate: null,
        accountOfficerStaffID: '',
        productCode: '',
        branchCode: '',
        useCurrentFinancialDate: true,
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      accountOfficerOptions: [],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      branchOptions: 'operations/branchOptions',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Name': item.accountName,
        'FD Account Number': item.accountNumber,
        'Liquidation Account Number': item.liquidationAccount,
        'Product Name': item.product,
        Branch: item.branch,
        'Account Officer': item.accountOfficer,
        'Tenure (Days)': item.tenure,
        'Interest Rate': item.interestRate,
        'Commencement Date': this.formatCustomDate(item.commencementDate),
        'Maturity Date': this.formatCustomDate(item.maturityDate),
        'Booked Amount': item.bookedAmount > 0 ? this.toAmount(item.bookedAmount) : '',
        'Accrued Interest': this.toAmount(item.accruedInterest),
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchFixedDepositBalanceReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchFixedDepositBalanceReport()
    },
  },
  mounted() {
    this.fetchFixedDepositBalanceReport()
    this.fetchProduct()
    this.fetchAccountOfficer()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranchByCode: 'operations/getBranchByCode',
      getFixedDepositBalance: 'reports/getFixedDepositBalance',
      getAccountOfficer: 'account/getAccountOfficerList',
    }),
    resetFilters() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDate = null
      this.filter.useCurrentFinancialDate = true
      this.fetchFixedDepositBalanceReport()
    },
    setUseCurrentFinancialDate(dataValue) {
      if (dataValue === false) {
        this.isDisabled = false
      } else {
        this.isDisabled = true
        this.filter.financialDate = null
      }
    },
    async fetchAccountOfficer() {
      try {
        const response = await this.getAccountOfficer({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.FIXED_DEPOSITS_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.FIXED_DEPOSITS_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },

    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      if (this.filter.useCurrentFinancialDate === false) {
        bvModalEvt.preventDefault()
        this.$refs.fixedDepositBalanceRules.validate().then(async success => {
          if (success) {
            this.fetchFixedDepositBalanceReport()
          }
        })
      } else {
        this.fetchFixedDepositBalanceReport()
      }
    },
    async fetchFixedDepositBalanceReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getFixedDepositBalance({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getFixedDepositBalance({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Fixed Deposit Balance Report'],
            [`${(this.filter.financialDate !== null) ? `As at ${this.formatCustomDate(this.filter.financialDate)}` : 'Date: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Fixed-Deposit-Balance-Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getFixedDepositBalance({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Fixed-Deposit-Balance-Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Fixed Deposit Balance Report'],
            [`${(this.filter.financialDate !== null) ? `As at ${this.formatCustomDate(this.filter.financialDate)}` : 'Date: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = arrData // All data lists
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Fixed Deposit Balance Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.getFixedDepositBalance({
          search: this.filter,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.isDisabled = false
          const pdfData = data.data.map(item => ({
            ...item,
            bookedAmount: item.bookedAmount > 0 ? this.toAmount(item.bookedAmount) : '',
            maturityDate: item.maturityDate ? this.formatCustomDate(item.maturityDate) : '',
            commencementDate: item.commencementDate ? this.formatCustomDate(item.commencementDate) : '',
            accruedInterest: this.toAmount(item.accruedInterest),
          }))

          const columns = [
            { title: 'Acc. Name', key: 'accountName' },
            { title: 'FD Acc. Number', key: 'accountNumber' },
            { title: 'Liq. Number', key: 'liquidationAccount' },
            { title: 'Product', key: 'product' },
            { title: 'Branch', key: 'branch' },
            { title: 'Acc. Officer', key: 'accountOfficer' },
            { title: 'Tenure', key: 'tenure' },
            { title: 'Int. Rate', key: 'interestRate' },
            { title: 'Commenc. Date', key: 'commencementDate' },
            { title: 'Maturity Date', key: 'maturityDate' },
            { title: 'Booked Amt', key: 'bookedAmount' },
            { title: 'Accrued Int.', key: 'accruedInterest' },
          ]

          const doc = jsPDF('landscape', 'pt', 'a4')
          const logo = this.reportInfo.map(item => item.orgLogo)
          const orgName = this.userDetails.institutionName.toUpperCase()
          const orgLogo = `${logo}`
          doc.addImage(orgLogo, 'JPEG', 40, 30)
          doc.setFontSize(20)
          doc.text(180, 60, `${orgName}`)
          doc.setFontSize(15)
          doc.text(180, 80, 'Fixed Deposit Balance Report')
          doc.setFontSize(12)
          doc.text(180, 95, `${(this.filter.financialDate !== null) ? `As at ${this.formatCustomDate(this.filter.financialDate)}` : 'Date: All'}`)
          doc.text(180, 110, `${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`)
          doc.autoTable({
            theme: 'striped', // grid, plain
            columns,
            body: pdfData,
            startY: 150,
            // split overflowing columns into pages
            horizontalPageBreak: true,
            // repeat this column in split pages
            horizontalPageBreakRepeat: 'id',
            tableWidth: 'auto', // 'auto', 'wrap' or a number,
            margin: {
              horizontal: 20,
              vertical: 20,
            },
            bodyStyles: {
              valign: 'top',
            },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap',
              maxCellHeight: 4,
              fontSize: 7,
              cellPadding: 5,
            },
            columnStyles: {
              text: {
                cellWidth: 'wrap',
              },
            },
          })
          // Footer page number
          const pages = doc.internal.getNumberOfPages()
          const pageWidth = doc.internal.pageSize.width
          const pageHeight = doc.internal.pageSize.height
          doc.setFontSize(5)

          for (let j = 1; j < pages + 1; j += 1) {
            const horizontalPos = pageWidth / 2 // Can be fixed number
            const verticalPos = pageHeight - 10 // Can be fixed number
            doc.setPage(j)
            doc.text(`page ${j} of ${pages}`, horizontalPos, verticalPos, { align: 'center' }) // Optional text styling});
          }
          doc.save('Fixed-Deposit-Balance-Report.pdf')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatAmount: toBalance,
    toAmount: formatAmount,
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
